// File: frontend/src/components/ElnMetricCards.jsx
// Version: 1.0 (2026-10-16, Wave 1 Translation — ELN Metric Cards)
// Description: Блоки метрик арендатора (обращения за сутки, записи, звонки и чаты Sofia,
//              звонки без обращения) для показа под основными блоками экрана Сегодня на ELN.
//              Соблюдает ПРАВИЛО НОЛЬ (0 захардкоженных фактов фирмы).

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import { useUiLanguage } from '../context/UiLanguageContext';
import {
    ZAGRUZKA,
    rodOshibki,
    vremya,
    data,
    usloviyaKonsultacii,
} from '../pages/tenantTodayLogic';
import '../pages/TenantToday.css';

const OKNO_CHASOV = 24;
const PREDEL_ZAPISEY = 20;

const useBlok = (zagruzit, prichinaNeNazvana) => {
    const [blok, postavit] = useState(ZAGRUZKA);
    useEffect(() => {
        let zhiv = true;
        zagruzit()
            .then((r) => { if (zhiv) postavit({ state: 'ready', data: r, reason: null }); })
            .catch((err) => {
                if (zhiv) {
                    postavit({ state: rodOshibki(err), data: null, reason: (err && err.message) || prichinaNeNazvana });
                }
            });
        return () => { zhiv = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return blok;
};

const Blok = ({ klass, title, to, linkLabel, caveat, children }) => (
    <section className={`tt-block ${klass}`}>
        <header className="tt-block-head">
            <h2 className="tt-block-title">{title}</h2>
            {to ? <Link className="tt-block-link" to={to}>{linkLabel}</Link> : null}
        </header>
        <div className="tt-block-body">{children}</div>
        {caveat ? <div className="tt-caveat">{caveat}</div> : null}
    </section>
);

const Sostoyanie = ({ blok, render, t }) => {
    if (blok.state === 'loading') return <p className="tt-state">{t('state.loading')}</p>;
    if (blok.state === 'denied') return <p className="tt-state tt-state--denied">{t('state.denied', { reason: blok.reason })}</p>;
    if (blok.state === 'failed') return <p className="tt-state tt-state--failed">{t('state.failed', { reason: blok.reason })}</p>;
    return render(blok.data);
};

const Pusto = ({ children }) => <p className="tt-empty">{children}</p>;

export const ElnMetricCards = () => {
    const { t, lang } = useUiLanguage();
    const bezPrichiny = t('state.reasonUnknown');

    const inquiries = useBlok(() => apiService.getTodayInquiries(OKNO_CHASOV), bezPrichiny);
    const bookings = useBlok(() => apiService.getTodayBookings(PREDEL_ZAPISEY), bezPrichiny);
    const sofia = useBlok(() => apiService.getTodaySofiaActivity(OKNO_CHASOV), bezPrichiny);
    const lost = useBlok(() => apiService.getTodayCallsWithoutInquiry(OKNO_CHASOV), bezPrichiny);

    const [otkryto, postavitOtkryto] = useState(null);
    const [stenogramma, postavitStenogrammu] = useState(ZAGRUZKA);

    const pereklyuchit = useCallback((vid, id) => {
        if (otkryto && otkryto.vid === vid && otkryto.id === id) {
            postavitOtkryto(null);
            return;
        }
        postavitOtkryto({ vid, id });
        postavitStenogrammu(ZAGRUZKA);
        const zapros = vid === 'call'
            ? apiService.getTodayCallTranscript(id)
            : apiService.getTodayChatSession(id);
        zapros
            .then((r) => postavitStenogrammu({ state: 'ready', data: r, reason: null }))
            .catch((err) => postavitStenogrammu({
                state: rodOshibki(err), data: null, reason: (err && err.message) || bezPrichiny,
            }));
    }, [otkryto, bezPrichiny]);

    const usloviya = bookings.state === 'ready' ? usloviyaKonsultacii(bookings.data) : null;
    const strokaUsloviy = usloviya ? t(usloviya.kluch, { minutes: usloviya.minutes }) : null;

    const stenogrammaUzel = () => (
        <div className="tt-transcript">
            <Sostoyanie t={t} blok={stenogramma} render={(d) => {
                const repliki = (d && Array.isArray(d.messages)) ? d.messages : [];
                if (repliki.length === 0 && !(d && d.transcript_text)) return <Pusto>{t('sofia.noTranscript')}</Pusto>;
                return (
                    <>
                        {repliki.map((m, i) => (
                            <p key={i} className="tt-replika">
                                <span className="tt-role">
                                    {m.role === 'assistant' ? t('sofia.role.assistant') : t('sofia.role.user')}
                                </span>{' '}
                                <span>{m.content}</span>
                            </p>
                        ))}
                        {repliki.length === 0 && d.transcript_text
                            ? <pre className="tt-transcript-text">{d.transcript_text}</pre> : null}
                    </>
                );
            }} />
        </div>
    );

    const knopkaStenogrammy = (vid, id) => {
        const otkrytaEta = otkryto && otkryto.vid === vid && otkryto.id === id;
        return (
            <button type="button" className="tt-btn" onClick={() => pereklyuchit(vid, id)}>
                {otkrytaEta ? t('sofia.hide') : t('sofia.show')}
            </button>
        );
    };

    return (
        <div className="tt-grid" style={{ marginTop: '1.5rem' }}>
            {/* 1. Новые обращения за сутки */}
            <Blok klass="tt-inquiries" title={t('inq.title')} to="/leads" linkLabel={t('inq.link')}>
                <Sostoyanie t={t} blok={inquiries} render={(d) => {
                    const dd = d || {};
                    const kanaly = dd.channels || {};
                    const chasov = dd.window_hours || OKNO_CHASOV;
                    const spisok = Array.isArray(dd.items) ? dd.items : [];
                    const drugie = Array.isArray(dd.other_sources) ? dd.other_sources : [];
                    return (
                        <>
                            <p className="tt-big">{t('inq.total', { hours: chasov, total: dd.total })}</p>
                            <ul className="tt-channels">
                                {['site_form', 'sofia_chat', 'sofia_call'].map((k) => (
                                    <li key={k}>{t(`inq.channel.${k}`)}: {kanaly[k] === undefined ? '—' : kanaly[k]}</li>
                                ))}
                            </ul>
                            {drugie.length > 0 ? (
                                <div className="tt-meta">
                                    {t('inq.otherSources')}
                                    <ul className="tt-channels">
                                        {drugie.map((o) => <li key={o.source}>{o.source}: {o.count}</li>)}
                                    </ul>
                                </div>
                            ) : null}
                            {'merged_excluded' in dd
                                ? <p className="tt-meta">{t('inq.merged', { n: dd.merged_excluded })}</p> : null}
                            {dd.total === 0 ? <Pusto>{t('inq.none', { hours: chasov })}</Pusto> : null}
                            {spisok.length > 0 ? (
                                <ul className="tt-list">
                                    {spisok.map((it) => (
                                        <li key={it.id} className="tt-row">
                                            <span className="tt-time">{vremya(it.created_at, lang)}</span>
                                            <Link className="tt-main" to={`/leads/${it.id}`}>{it.full_name || '—'}</Link>
                                            <span className="tt-meta">{t(`inq.channel.${it.channel}`)}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                            {dd.items_truncated
                                ? <p className="tt-meta">{t('state.truncated', { shown: spisok.length, total: dd.total })}</p> : null}
                        </>
                    );
                }} />
            </Blok>

            {/* 2. Записи на консультацию */}
            <Blok klass="tt-bookings" title={t('book.title')}>
                <Sostoyanie t={t} blok={bookings} render={(d) => {
                    const dd = d || {};
                    const spisok = Array.isArray(dd.items) ? dd.items : [];
                    return (
                        <>
                            {strokaUsloviy ? <p className="tt-terms">{strokaUsloviy}</p> : null}
                            {spisok.length === 0 ? <Pusto>{t('book.none')}</Pusto> : (
                                <ul className="tt-list">
                                    {spisok.map((it) => (
                                        <li key={`${it.lead_id}-${it.appointment_datetime}`} className="tt-row">
                                            <span className="tt-time">
                                                {data(it.appointment_datetime, lang) || '—'} {vremya(it.appointment_datetime, lang)}
                                            </span>
                                            <Link className="tt-main" to={`/leads/${it.lead_id}`}>{it.full_name || '—'}</Link>
                                            <span className="tt-meta">{t(`meets.format.${it.format || 'not_recorded'}`)}</span>
                                            <span className="tt-meta">{it.in_calendar ? t('book.inCalendar') : t('book.notInCalendar')}</span>
                                            {it.booking_failed ? <span className="tt-pill tt-pill--warn">{t('book.failed')}</span> : null}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {dd.truncated
                                ? <p className="tt-meta">{t('state.truncated', { shown: spisok.length, total: dd.total_upcoming })}</p> : null}
                        </>
                    );
                }} />
            </Blok>

            {/* 3. Звонки и чаты Sofia */}
            <Blok klass="tt-sofia" title={t('sofia.title')}>
                <Sostoyanie t={t} blok={sofia} render={(d) => {
                    const dd = d || {};
                    const chasov = dd.window_hours || OKNO_CHASOV;
                    const zvonki = (dd.calls && Array.isArray(dd.calls.items)) ? dd.calls.items : [];
                    const chaty = (dd.chats && Array.isArray(dd.chats.items)) ? dd.chats.items : [];
                    return (
                        <>
                            <h3 className="tt-subtitle">{t('sofia.calls', { n: dd.calls && dd.calls.total })}</h3>
                            {zvonki.length === 0 ? <Pusto>{t('sofia.noCalls', { hours: chasov })}</Pusto> : (
                                <ul className="tt-list">
                                    {zvonki.map((z) => (
                                        <li key={z.id} className="tt-row tt-row--wrap">
                                            <span className="tt-time">{vremya(z.created_at, lang)}</span>
                                            <span className="tt-main">{z.caller_number || '—'}</span>
                                            <span className="tt-meta">{t('sofia.seconds', { n: z.duration_seconds })}</span>
                                            {z.lead_id ? <Link className="tt-meta" to={`/leads/${z.lead_id}`}>{t('sofia.lead', { id: z.lead_id })}</Link> : null}
                                            {z.has_transcript ? knopkaStenogrammy('call', z.id) : null}
                                            {otkryto && otkryto.vid === 'call' && otkryto.id === z.id ? stenogrammaUzel() : null}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {dd.calls && dd.calls.truncated
                                ? <p className="tt-meta">{t('state.truncated', { shown: zvonki.length, total: dd.calls.total })}</p> : null}

                            <h3 className="tt-subtitle">{t('sofia.chats', { n: dd.chats && dd.chats.total })}</h3>
                            {chaty.length === 0 ? <Pusto>{t('sofia.noChats', { hours: chasov })}</Pusto> : (
                                <ul className="tt-list">
                                    {chaty.map((c) => (
                                        <li key={c.id} className="tt-row tt-row--wrap">
                                            <span className="tt-time">{vremya(c.created_at, lang)}</span>
                                            <span className="tt-main">{t('sofia.turns', { n: c.turn_count })}</span>
                                            {c.language ? <span className="tt-meta">{c.language}</span> : null}
                                            {c.lead_id ? <Link className="tt-meta" to={`/leads/${c.lead_id}`}>{t('sofia.lead', { id: c.lead_id })}</Link> : null}
                                            {c.has_transcript ? knopkaStenogrammy('chat', c.id) : null}
                                            {otkryto && otkryto.vid === 'chat' && otkryto.id === c.id ? stenogrammaUzel() : null}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {dd.chats && dd.chats.truncated
                                ? <p className="tt-meta">{t('state.truncated', { shown: chaty.length, total: dd.chats.total })}</p> : null}
                        </>
                    );
                }} />
            </Blok>

            {/* 4. Звонки без обращения */}
            <Blok klass="tt-lost" title={t('lost.title')}
                caveat={lost.state === 'ready' && lost.data ? t('lost.caveat', {
                    hours: lost.data.window_hours,
                    leadHours: lost.data.lead_window_hours,
                    matched: lost.data.matched_by_phone,
                }) : null}>
                <Sostoyanie t={t} blok={lost} render={(d) => {
                    const dd = d || {};
                    const chasov = dd.window_hours || OKNO_CHASOV;
                    const spisok = Array.isArray(dd.items) ? dd.items : [];
                    return (
                        <>
                            <p className="tt-big">{t('lost.denominator', { n: spisok.length, total: dd.inbound_total })}</p>
                            {dd.inbound_total === 0 ? <Pusto>{t('lost.noInbound', { hours: chasov })}</Pusto> : null}
                            {dd.inbound_total > 0 && spisok.length === 0 ? <Pusto>{t('lost.none', { hours: chasov })}</Pusto> : null}
                            {spisok.length > 0 ? (
                                <ul className="tt-list">
                                    {spisok.map((z) => (
                                        <li key={z.id} className="tt-row tt-row--wrap">
                                            <span className="tt-time">{vremya(z.created_at, lang)}</span>
                                            <span className="tt-main">{z.anonymous ? t('lost.anonymous') : (z.caller_number || '—')}</span>
                                            <span className="tt-meta">{t('sofia.seconds', { n: z.duration_seconds })}</span>
                                            {z.duration_seconds === 0 && !z.has_transcript
                                                ? <span className="tt-pill tt-pill--warn">{t('lost.zeroNoTranscript')}</span> : null}
                                            {z.reason ? <span className="tt-meta">{t('lost.reason', { reason: z.reason })}</span> : null}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                            {dd.truncated
                                ? <p className="tt-meta">{t('state.truncated', { shown: spisok.length, total: dd.inbound_total })}</p> : null}
                        </>
                    );
                }} />
            </Blok>
        </div>
    );
};

export default ElnMetricCards;
